import React, { useState } from 'react';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  SectionHeader,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { candidateService } from '../../../api';
import {
  SubjectDetails,
  ViewSelectedSubjectPapers,
  ViewSelectedSubjectsResponseBody,
} from '../../../api/candidate/exam/model';
import { useExamNavigation } from '../../../routes/ExamRoute';
import DeleteSubjectModal from './DeleteSubjectModal';
import {
  GET_EXAM_REGISTRATION,
  GET_SELECTED_SUBJECTS,
} from '../../../api/candidate/exam/@candidate/queries-services/keys';
import ExamDraftMessage from './ExamDraftMessage';
import EditExamSubjectsModal, {
  IEditStateHandler,
} from '../../../components/EditExamSubjectsModal';
import { onEditModalOpenHandler } from '../../../utils/ApiUtils';
import { getKeyByValue } from '../../../utils/ArrayUtils';
import DeleteButton from '../../../components/buttons/DeleteButton';
import EditButton from '../../../components/buttons/EditButton';
import { usePostSubjectValidation } from '../../subject/logic/useSubjectAmendment';
import { setSubjectDetails } from '../../../utils/StringUtils';

import {typographyTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  textContainer: {
    marginBottom: theme.spacing(3),
  },
  subjectContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 auto',
  },
  subjectText: {
    paddingBottom: theme.spacing(1),
  },
  editContainer: {
    marginBottom: theme.spacing(6),
  },
  deleteButton: {
    color: '#E55934',
  },
  deleteContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  button: {
    height: 'fit-content',
    padding: theme.spacing(2),
    minHeight: 0,
    minWidth: 0,
    text: {
      padding: '0 !important',
    },
  },
  buttonContainer: {
    display: 'flex',
  },
  icon: {
    margin: 'auto',
    marginRight: '12px',
    // paddingLeft: 0,
    marginLeft: 0,
  },
  iconDefault: {
    color: theme.color.link.linkDefault01,
  },
  iconDelete: {
    color: '#E55934',
  },
  optionalPapers: {
     ...typographyTheme(theme, 'subtitle2'),
    color: theme.color.text.textSecondary,
  },
  errorMsgContainer: {
    marginBottom: theme.spacing(1),
  },
  subjectValidationMsg: {
    fontWeight: 600,
    color: theme.color.state.stateError,
  },
}));

interface ExamViewSelectedSubjectPath {
  uuid?: string;
}

const useCallApis = (uuid: string) => {
  const { snackbarHandler } = useSnackbarHandler();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal] = useState(false);
  const [
    actionTargetSubject,
    setActionTargetSubject,
  ] = useState<ViewSelectedSubjectsResponseBody>();

  const { data: selectedSubjects = [] } = candidateService.exam.candidate.useGetSelectedSubjects(
    uuid
  );

  const {
    mutate: deleteSelectedSubjectMutate,
    status: deleteSelectedSubjectStatus,
    reset: deleteSelectedSubjectReset,
  } = candidateService.exam.candidate.useDeleteExamSubjects(
    (res, variable) => {
      onDeleteModalCloseHandler();
      queryClient.invalidateQueries(GET_EXAM_REGISTRATION);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>SubjectSnackbar.HasBeenRemoved', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
      });
      queryClient.invalidateQueries([GET_SELECTED_SUBJECTS, uuid]);
    },
    (err, variable) => {
      onDeleteModalCloseHandler();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('exam->>SubjectSnackbar.UnableToRemove', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    }
  );

  const onDeleteModalConfirmHandler = (
    examRegistrationUuid: string,
    examRegistrationDetailUuid: string,
    subjectDetails: SubjectDetails
  ) => {
    deleteSelectedSubjectMutate({
      applicationUuid: examRegistrationUuid,
      examRegistrationDetailUuid,
      callbackVariable: subjectDetails,
    });
  };

  const onDeleteSelectedSubject = (selectedSubject: ViewSelectedSubjectsResponseBody) => {
    if (selectedSubject) {
      setOpenDeleteModal(true);
      setActionTargetSubject(selectedSubject);
    }
  };

  const onDeleteModalCloseHandler = () => {
    setOpenDeleteModal(false);
    setActionTargetSubject(undefined);
    deleteSelectedSubjectReset();
  };

  return {
    selectedSubjects,
    deleteSelectedSubjectStatus,
    openDeleteModal,
    openEditModal,
    actionTargetSubject,
    onDeleteModalCloseHandler,
    onDeleteSelectedSubject,
    onDeleteModalConfirmHandler,
  };
};

interface IOptions {
  selectedSubjects: ViewSelectedSubjectsResponseBody[];
  classes: any;
  onDeleteSelectedSubject: (selectedSubject: ViewSelectedSubjectsResponseBody) => void;
  openEditModalStateHandler: IEditStateHandler;
  applicationUuid: string;
  t: any;
}

const DeleteButtonOptions = (deleteButton: any) => ({
  options: {
    sort: false,
    searchable: false,
    customBodyRenderLite: (dataIndex: number, rowIndex: number) => deleteButton(dataIndex),
  },
});

const EditButtonOptions = (
  editButton: any,
  selectedSubjects: IOptions['selectedSubjects'],
  display: boolean
) => ({
  options: {
    sort: false,
    searchable: false,
    display,
    customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
      <>{selectedSubjects[dataIndex].editable && editButton(dataIndex)}</>
    ),
  },
});

const dataTableColumns = (args: IOptions): MUIDataTableColumnDef[] => {
  const { selectedSubjects, classes, onDeleteSelectedSubject, openEditModalStateHandler, t } = args;
  const deleteButton = (dataIndex: number) =>
    DeleteButton({
      deleteHandler: () => onDeleteSelectedSubject(selectedSubjects[dataIndex]),
      label: t('component->>IconLabel.Remove'),
    });
  const checkEditColumn = () => selectedSubjects.find((subject) => subject.editable);
  const checkToRenderEditColumn = !!checkEditColumn();
  const editButton = (dataIndex: number) =>
    EditButton({
      editHandler: () =>
        onEditModalOpenHandler(
          selectedSubjects[dataIndex].examRegistrationDetailUuid,
          openEditModalStateHandler
        ),
      label: t('component->>IconLabel.Edit'),
    });
  return [
    {
      name: 'subjectName',
      label: t('exam->>ViewSelectedSubjects.Subject Name'),
      options: {
        sort: false,
        searchable: false,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => {
          const optionalPaperList: string[] = [];
          selectedSubjects[dataIndex].optionalPapers.forEach((item: ViewSelectedSubjectPapers) =>
            optionalPaperList.push(`${t('exam->>ViewSelectedSubjects.Paper')} ${item.paperNo}`)
          );

          return (
            <Box className={classes.subjectContainer}>
              <Typography variant="body2" className={classes.subjectText}>
                {selectedSubjects[dataIndex].subjectName} ({selectedSubjects[dataIndex].subjectCode}
                )
              </Typography>
              {optionalPaperList.length ? (
                <Typography variant="body2" className={classes.optionalPapers}>
                  {t('exam->>ViewSelectedSubjects.OptionalPapers')}: {optionalPaperList.join(', ')}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
    },
    {
      name: 'subjectLevel',
      label: t('exam->>ViewSelectedSubjects.Subject Level'),
      options: {
        sort: false,
        searchable: false,
      },
    },
    {
      name: 'examSeries',
      label: t('exam->>ViewSelectedSubjects.Exam Series'),
      options: {
        sort: false,
        searchable: false,
      },
    },
    {
      name: 'examLevel',
      label: t('exam->>ViewSelectedSubjects.Exam Level'),
      options: {
        sort: false,
        searchable: false,
      },
    },
    {
      name: 'language',
      label: t('exam->>ViewSelectedSubjects.Language'),
      options: {
        sort: false,
        searchable: false,
      },
    },
    {
      name: 'action',
      label: t('exam->>ViewSelectedSubjects.Action'),
      options: EditButtonOptions(editButton, selectedSubjects, checkToRenderEditColumn).options,
    },
    {
      name: `${checkToRenderEditColumn ? '' : 'action'}`,
      label: `${checkToRenderEditColumn ? '' : t('exam->>ViewSelectedSubjects.Action')}`,
      options: DeleteButtonOptions(deleteButton).options,
    },
  ];
};

const ExamViewSelectedSubjects = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid = '' } = useParams<ExamViewSelectedSubjectPath>();
  const { state } = useLocation<{ token: string }>();
  const [openEditModal, setOpenEditModal] = useState<Record<string, boolean>>({});
  const openEditModalStateHandler = { openEditModal, setOpenEditModal };
  const {
    selectedSubjects,
    deleteSelectedSubjectStatus,
    openDeleteModal,
    actionTargetSubject,
    onDeleteModalConfirmHandler,
    onDeleteModalCloseHandler,
    onDeleteSelectedSubject,
  } = useCallApis(uuid);
  const { toReviewParticulars, toSelectSubject } = useExamNavigation();

  const backToSelectSubject = () => {
    history.push(toSelectSubject.replace(':uuid', uuid), { token: state && state.token });
  };

  const {
    subjectValidationMsg,
    postSubjectValidation,
    apiStatus,
    setApiStatus,
  } = usePostSubjectValidation(toReviewParticulars.replace(':uuid', uuid));

  const confirmSelectedSubject = () => {
    postSubjectValidation.mutate(uuid);
    setApiStatus('loading');
  };

  const tableColumns = dataTableColumns({
    selectedSubjects,
    classes,
    applicationUuid: uuid,
    onDeleteSelectedSubject,
    openEditModalStateHandler: {
      openEditModal,
      setOpenEditModal,
    },
    t,
  });

  return (
    <>
      {actionTargetSubject && (
        <DeleteSubjectModal
          modelOpen={openDeleteModal}
          subjectDetails={actionTargetSubject}
          applicationUuid={uuid}
          deleteSubjectAction={{
            onClose: onDeleteModalCloseHandler,
            onConfirm: onDeleteModalConfirmHandler,
            apiStatus: deleteSelectedSubjectStatus,
          }}
        />
      )}
      <EditExamSubjectsModal
        applicationUuid={uuid}
        examRegistrationDetailUuid={getKeyByValue(openEditModal, true)}
        key={getKeyByValue(openEditModal, true)}
        editModalHandler={openEditModalStateHandler}
      />
      <Box>
        <ContentTemplate isContinuable type="full">
          <SectionHeader title={t('exam->>ViewSelectedSubjects.ViewSelectedSubjects')} />
          <Box className={classes.editContainer}>
            {selectedSubjects && (
              <MUIDataTable
                columns={tableColumns}
                data={selectedSubjects}
                emptyDataLabel={t('exam->>ViewSelectedSubjects.EmptyDataTableContent')}
              />
            )}
          </Box>

          <Box className={classes.errorMsgContainer}>
            {subjectValidationMsg.length > 0 &&
              subjectValidationMsg.map((errorMsg) => (
                <Typography key={errorMsg} className={classes.subjectValidationMsg} variant="body2">
                  {errorMsg}
                </Typography>
              ))}
          </Box>
        </ContentTemplate>

        <ContentTemplate continuous>
          <Grid container direction="row" alignItems="flex-start" spacing={3}>
            <Grid xs={12} md={4}>
              <Button
                id="button-viewSelectedSubjects-back"
                fullWidth
                className={classes.button}
                variant="outlined"
                onClick={() => {
                  backToSelectSubject();
                }}
              >
                {t('exam->>ViewSelectedSubjects.AddMoreSubjects')}
              </Button>
            </Grid>

            <Grid xs={12} md={5}>
              <ApiButton
                id="button-viewSelectedSubjects-next"
                fullWidth
                className={classes.button}
                disabled={!selectedSubjects.length}
                variant="contained"
                onClick={() => {
                  confirmSelectedSubject();
                }}
                status={apiStatus}
              >
                {t('exam->>ViewSelectedSubjects.ProceedReviewParticulars')}
              </ApiButton>
            </Grid>
          </Grid>
          <ExamDraftMessage />
        </ContentTemplate>
      </Box>
    </>
  );
};
export default ExamViewSelectedSubjects;
